import React, { useState, useRef, useEffect } from 'react';
import { ArrowLeft, ClipboardCheck, Edit } from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import { RetrofitSurveyData } from '../../utils/retrofitSurveyStorage';
import SignatureModal from '../SignatureModal';

interface RetrofitSignaturesFormProps {
  onNavigateNext: () => void;
  onNavigateBack: () => void;
  surveyData?: RetrofitSurveyData;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData, sectionId?: string) => void;
  onSaveImmediately?: () => Promise<string>;
}

interface CustomerSignature {
  statement1: string; // Retrofit Assessor completion confirmation
  statement2: string; // Results via Retrofit Advisor
  statement3: string; // OFGEM scheme data sharing
  statement4: string; // Homeowner confirmation
  statement1Date: string;
  statement2Date: string;
  statement3Date: string;
  statement4Date: string;
}

interface SurveyorSignature {
  statement1: string; // Collected all evidence per Retrofit Assessment scheme
  statement2: string; // GDPR rules per Eco Approach Ltd
  statement3: string; // Informed customer about PAS 2035
  statement4: string; // Customer property and GDPR care
  statement5: string; // Survey compliance check
  statement1Date: string;
  statement2Date: string;
  statement3Date: string;
  statement4Date: string;
  statement5Date: string;
}

interface Feedback {
  rating: number | null;
  comments: string;
}

interface PreSignatureQuestions {
  asbestosChecksUndertaken: boolean | null;
  servicesAffectedByInstallation: boolean | null;
  potentialRiskToServices: boolean | null;
  installationInLineWithDesign: boolean | null;
  methodStatementSuitable: boolean | null;
  protectedSpeciesDisturbance: boolean | null;
  customerUnderstandsWork: boolean | null;
  customerAwareOfDisruption: boolean | null;
  customerHasRequests: boolean | null;
  siteAccessArrangementsAdequate: boolean | null;
  otherInstallationInfo: boolean | null;
  potentialMoistureBuildUp: boolean | null;
}

const RetrofitSignaturesForm: React.FC<RetrofitSignaturesFormProps> = ({
  onNavigateNext,
  onNavigateBack,
  surveyData,
  onDataUpdate,
  onSaveImmediately
}) => {
  // Signature refs
  const customerStatement1Ref = useRef<SignatureCanvas>(null);
  const customerStatement2Ref = useRef<SignatureCanvas>(null);
  const customerStatement3Ref = useRef<SignatureCanvas>(null);
  const customerStatement4Ref = useRef<SignatureCanvas>(null);
  const surveyorStatement1Ref = useRef<SignatureCanvas>(null);
  const surveyorStatement2Ref = useRef<SignatureCanvas>(null);
  const surveyorStatement3Ref = useRef<SignatureCanvas>(null);
  const surveyorStatement4Ref = useRef<SignatureCanvas>(null);
  const surveyorStatement5Ref = useRef<SignatureCanvas>(null);

  // State for signature modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureRef, setCurrentSignatureRef] = useState<React.RefObject<SignatureCanvas> | null>(null);
  const [currentSignatureType, setCurrentSignatureType] = useState<string>('');

  const [customerSignatures, setCustomerSignatures] = useState<CustomerSignature>({
    statement1: surveyData?.signatures?.customer?.statement1 || '',
    statement2: surveyData?.signatures?.customer?.statement2 || '',
    statement3: surveyData?.signatures?.customer?.statement3 || '',
    statement4: surveyData?.signatures?.customer?.statement4 || '',
    statement1Date: surveyData?.signatures?.customer?.statement1Date || '',
    statement2Date: surveyData?.signatures?.customer?.statement2Date || '',
    statement3Date: surveyData?.signatures?.customer?.statement3Date || '',
    statement4Date: surveyData?.signatures?.customer?.statement4Date || ''
  });

  const [surveyorSignatures, setSurveyorSignatures] = useState<SurveyorSignature>({
    statement1: surveyData?.signatures?.surveyor?.statement1 || '',
    statement2: surveyData?.signatures?.surveyor?.statement2 || '',
    statement3: surveyData?.signatures?.surveyor?.statement3 || '',
    statement4: surveyData?.signatures?.surveyor?.statement4 || '',
    statement5: surveyData?.signatures?.surveyor?.statement5 || '',
    statement1Date: surveyData?.signatures?.surveyor?.statement1Date || '',
    statement2Date: surveyData?.signatures?.surveyor?.statement2Date || '',
    statement3Date: surveyData?.signatures?.surveyor?.statement3Date || '',
    statement4Date: surveyData?.signatures?.surveyor?.statement4Date || '',
    statement5Date: surveyData?.signatures?.surveyor?.statement5Date || ''
  });

  const [feedback, setFeedback] = useState<Feedback>({
    rating: surveyData?.signatures?.feedback?.rating || null,
    comments: surveyData?.signatures?.feedback?.comments || ''
  });

  const [preSignatureQuestions, setPreSignatureQuestions] = useState<PreSignatureQuestions>({
    asbestosChecksUndertaken: surveyData?.signatures?.asbestosChecksUndertaken ?? null,
    servicesAffectedByInstallation: surveyData?.signatures?.servicesAffectedByInstallation ?? null,
    potentialRiskToServices: surveyData?.signatures?.potentialRiskToServices ?? null,
    installationInLineWithDesign: surveyData?.signatures?.installationInLineWithDesign ?? null,
    methodStatementSuitable: surveyData?.signatures?.methodStatementSuitable ?? null,
    protectedSpeciesDisturbance: surveyData?.signatures?.protectedSpeciesDisturbance ?? null,
    customerUnderstandsWork: surveyData?.signatures?.customerUnderstandsWork ?? null,
    customerAwareOfDisruption: surveyData?.signatures?.customerAwareOfDisruption ?? null,
    customerHasRequests: surveyData?.signatures?.customerHasRequests ?? null,
    siteAccessArrangementsAdequate: surveyData?.signatures?.siteAccessArrangementsAdequate ?? null,
    otherInstallationInfo: surveyData?.signatures?.otherInstallationInfo ?? null,
    potentialMoistureBuildUp: surveyData?.signatures?.potentialMoistureBuildUp ?? null
  });

  // Lock body scroll when signature modal is open
  useEffect(() => {
    if (showSignatureModal) {
      console.log('=== APPLYING BODY SCROLL LOCK ===');
      console.log('Scroll position before lock:', window.scrollY);
      
      // Simple overflow hidden approach - doesn't affect fixed positioning
      const originalOverflow = document.body.style.overflow;
      const originalPaddingRight = document.body.style.paddingRight;
      
      // Prevent scrollbar shift by adding padding
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      console.log('Body styles after lock:', {
        overflow: document.body.style.overflow,
        paddingRight: document.body.style.paddingRight
      });
      
      // Cleanup function
      return () => {
        console.log('=== REMOVING BODY SCROLL LOCK ===');
        
        // Restore body scroll
        document.body.style.overflow = originalOverflow;
        document.body.style.paddingRight = originalPaddingRight;
        
        console.log('Body styles restored');
      };
    }
  }, [showSignatureModal]);

  const openSignatureModal = (type: string, ref: React.RefObject<SignatureCanvas>) => {
    // Log user's current position
    const userScrollY = window.scrollY;
    const viewportHeight = window.innerHeight;
    const viewportTop = userScrollY;
    const viewportBottom = userScrollY + viewportHeight;
    
    console.log('=== MODAL OPEN REQUESTED ===');
    console.log('User scroll position (Y):', userScrollY);
    console.log('Viewport height:', viewportHeight);
    console.log('Viewport top:', viewportTop);
    console.log('Viewport bottom:', viewportBottom);
    console.log('Signature type:', type);
    
    setCurrentSignatureType(type);
    setCurrentSignatureRef(ref);
    setShowSignatureModal(true);
    
    // Load existing signature into canvas if it exists
    setTimeout(() => {
      if (ref.current) {
        let existingSignature = '';
        
        if (type.startsWith('customer')) {
          const statementNum = type.replace('customer', '');
          existingSignature = customerSignatures[`statement${statementNum}` as keyof CustomerSignature] as string || '';
        } else if (type.startsWith('surveyor')) {
          const statementNum = type.replace('surveyor', '');
          existingSignature = surveyorSignatures[`statement${statementNum}` as keyof SurveyorSignature] as string || '';
        }
        
        if (existingSignature) {
          console.log('=== LOADING EXISTING SIGNATURE ===');
          console.log('Signature data URL length:', existingSignature.length);
          console.log('Data URL preview:', existingSignature.substring(0, 50) + '...');
          
          const canvas = ref.current.getCanvas();
          console.log('Canvas before loading:', {
            width: canvas.width,
            height: canvas.height,
            visualWidth: canvas.getBoundingClientRect().width,
            visualHeight: canvas.getBoundingClientRect().height
          });
          
          ref.current.fromDataURL(existingSignature);
          
          setTimeout(() => {
            console.log('Canvas after loading:', {
              isEmpty: ref.current?.isEmpty(),
              width: canvas.width,
              height: canvas.height
            });
          }, 50);
        } else {
          console.log('No existing signature to load');
          ref.current.clear();
        }
      } else {
        console.error('Canvas ref not available!');
      }
    }, 150);
  };

  const handleSaveSignature = () => {
    if (currentSignatureRef?.current && !currentSignatureRef.current.isEmpty()) {
      const dataURL = currentSignatureRef.current.toDataURL();
      const currentDate = new Date().toLocaleDateString('en-GB');

      if (currentSignatureType.startsWith('customer')) {
        const statementNum = currentSignatureType.replace('customer', '');
        setCustomerSignatures(prev => ({
          ...prev,
          [`statement${statementNum}`]: dataURL,
          [`statement${statementNum}Date`]: currentDate
        }));
        
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            signatures: {
              ...prev.signatures,
              customer: {
                ...prev.signatures?.customer,
                [`statement${statementNum}`]: dataURL,
                [`statement${statementNum}Date`]: currentDate
              }
            }
          }), 'signatures');
        }
      } else if (currentSignatureType.startsWith('surveyor')) {
        const statementNum = currentSignatureType.replace('surveyor', '');
        setSurveyorSignatures(prev => ({
          ...prev,
          [`statement${statementNum}`]: dataURL,
          [`statement${statementNum}Date`]: currentDate
        }));
        
        if (onDataUpdate) {
          onDataUpdate(prev => ({
            ...prev,
            signatures: {
              ...prev.signatures,
              surveyor: {
                ...prev.signatures?.surveyor,
                [`statement${statementNum}`]: dataURL,
                [`statement${statementNum}Date`]: currentDate
              }
            }
          }), 'signatures');
        }
      }
    }
    setShowSignatureModal(false);
    currentSignatureRef?.current?.clear();
  };

  const handleClearSignature = () => {
    currentSignatureRef?.current?.clear();
  };

  const handleDeleteSignature = () => {
    const type = currentSignatureType;
    if (type.startsWith('customer')) {
      const statementNum = type.replace('customer', '');
      setCustomerSignatures(prev => ({
        ...prev,
        [`statement${statementNum}`]: '',
        [`statement${statementNum}Date`]: ''
      }));

      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            customer: {
              ...prev.signatures?.customer,
              [`statement${statementNum}`]: '',
              [`statement${statementNum}Date`]: ''
            }
          }
        }), 'signatures');
      }
    } else if (type.startsWith('surveyor')) {
      const statementNum = type.replace('surveyor', '');
      setSurveyorSignatures(prev => ({
        ...prev,
        [`statement${statementNum}`]: '',
        [`statement${statementNum}Date`]: ''
      }));

      if (onDataUpdate) {
        onDataUpdate(prev => ({
          ...prev,
          signatures: {
            ...prev.signatures,
            surveyor: {
              ...prev.signatures?.surveyor,
              [`statement${statementNum}`]: '',
              [`statement${statementNum}Date`]: ''
            }
          }
        }), 'signatures');
      }
    }
    
    // Close modal after deletion
    setShowSignatureModal(false);
    currentSignatureRef?.current?.clear();
  };

  const handleRatingChange = (rating: number) => {
    setFeedback(prev => ({ ...prev, rating }));
    
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          feedback: {
            ...prev.signatures?.feedback,
            rating
          }
        }
      }), 'signatures');
    }
  };

  const handlePreSignatureQuestionChange = (field: keyof PreSignatureQuestions, value: boolean | null) => {
    setPreSignatureQuestions(prev => ({ ...prev, [field]: value }));
    
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          [field]: value
        }
      }), 'signatures');
    }
  };

  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const comments = e.target.value;
    setFeedback(prev => ({ ...prev, comments }));
    
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        signatures: {
          ...prev.signatures,
          feedback: {
            ...prev.signatures?.feedback,
            comments
          }
        }
      }), 'signatures');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNavigateNext();
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm p-6 sm:p-8 rounded-xl shadow-lg border border-yellow-200 min-h-[calc(100vh-12rem)] flex flex-col">
      <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
        <div className="flex-1">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 border-b-2 border-gray-200 pb-4 mb-6 flex items-center gap-3">
            <ClipboardCheck />
            Survey Sign Off & Feedback
          </h2>

          {/* Pre-Signature Assessment Questions */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-purple-700 mb-4">Pre-Installation Assessment</h3>
            
            {/* Asbestos Checks */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Have you determined if relevant checks have been undertaken to determine if materials containing asbestos are present?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('asbestosChecksUndertaken', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.asbestosChecksUndertaken === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('asbestosChecksUndertaken', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.asbestosChecksUndertaken === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Services Affected */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Will the functionality and/or safety of installed services (gas, electricity, water, telecommunications etc.) be affected in any way as a result of the installation?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('servicesAffectedByInstallation', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.servicesAffectedByInstallation === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('servicesAffectedByInstallation', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.servicesAffectedByInstallation === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Potential Risk to Services */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Is there any potential risk in relation to the ongoing performance of installed services? (If YES liaison with the Retrofit Designer and / or Retrofit Coordinator will need to take place).
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('potentialRiskToServices', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.potentialRiskToServices === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('potentialRiskToServices', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.potentialRiskToServices === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Installation in Line with Design */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                From the findings of this building inspection, is the installation able to be carried out in line with the full Design Specification?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('installationInLineWithDesign', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.installationInLineWithDesign === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('installationInLineWithDesign', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.installationInLineWithDesign === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Method Statement Suitable */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                From the findings of this building inspection, is the Method Statement suitable and complete?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('methodStatementSuitable', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.methodStatementSuitable === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('methodStatementSuitable', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.methodStatementSuitable === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Protected Species */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Is there any evidence that any form of protected species would be disturbed by the installation process?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('protectedSpeciesDisturbance', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.protectedSpeciesDisturbance === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('protectedSpeciesDisturbance', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.protectedSpeciesDisturbance === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>
          </div>

          {/* PAS 2030 General Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-indigo-700 mb-4">PAS 2030 General</h3>
            
            {/* Customer Understands Work */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Does the customer understand the nature and extent of the work?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerUnderstandsWork', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerUnderstandsWork === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerUnderstandsWork', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerUnderstandsWork === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Customer Aware of Disruption */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Have they been made aware of any disruption that may be caused (eg rubbish disposal and the siting of skips)?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerAwareOfDisruption', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerAwareOfDisruption === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerAwareOfDisruption', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerAwareOfDisruption === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Customer Requests */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Does the customer have any particular requests or requirements?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerHasRequests', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerHasRequests === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('customerHasRequests', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.customerHasRequests === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Site Access */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Are the arrangements for site access and the storage of installation materials adequate and appropriate?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('siteAccessArrangementsAdequate', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.siteAccessArrangementsAdequate === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('siteAccessArrangementsAdequate', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.siteAccessArrangementsAdequate === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Other Installation Info */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Is there any other information which would help ensure the smooth running of the installation process?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('otherInstallationInfo', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.otherInstallationInfo === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('otherInstallationInfo', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.otherInstallationInfo === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>

            {/* Moisture Build Up */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4 bg-gray-50">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                Is there any potential for moisture build up as a result of the installation?
              </p>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('potentialMoistureBuildUp', true)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.potentialMoistureBuildUp === true
                      ? 'bg-blue-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  YES
                </button>
                <button
                  type="button"
                  onClick={() => handlePreSignatureQuestionChange('potentialMoistureBuildUp', false)}
                  className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                    preSignatureQuestions.potentialMoistureBuildUp === false
                      ? 'bg-gray-600 text-white'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  NO
                </button>
              </div>
            </div>
          </div>

          {/* Customer Sign Off Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-blue-700 mb-4">Customer Sign Off</h3>

            {/* Statement 1 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that the Retrofit Assessor has completed the survey in my property and I am happy with the way he has explained me before the survey.
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('customer1', customerStatement1Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Edit size={16} />
                  {customerSignatures.statement1 ? 'Change Signature' : 'Add Signature'}
                </button>
                {customerSignatures.statement1 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={customerSignatures.statement1} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {customerSignatures.statement1Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Statement 2 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I understand that the result of the survey will be provided by the Office via Retrofit Advisor and I can decide from there if I am happy with the installation.
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('customer2', customerStatement2Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Edit size={16} />
                  {customerSignatures.statement2 ? 'Change Signature' : 'Add Signature'}
                </button>
                {customerSignatures.statement2 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={customerSignatures.statement2} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {customerSignatures.statement2Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Statement 3 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I do understand that the Retrofit Assessment completed was for the OFGEM scheme and all my data will be stored and shared with OFGEM scheme providers only for the scheme purpose
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('customer3', customerStatement3Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Edit size={16} />
                  {customerSignatures.statement3 ? 'Change Signature' : 'Add Signature'}
                </button>
                {customerSignatures.statement3 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={customerSignatures.statement3} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {customerSignatures.statement3Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Statement 4 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that I am the homeowner/tenant staying in the property and all the information provided by me for the scheme eligibility is true and no fraudulent documents has been provided by me to the installers
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('customer4', customerStatement4Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Edit size={16} />
                  {customerSignatures.statement4 ? 'Change Signature' : 'Add Signature'}
                </button>
                {customerSignatures.statement4 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={customerSignatures.statement4} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {customerSignatures.statement4Date}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Feedback Section */}
          <div className="mb-8 border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              <span className="text-red-500">* </span>Please rate the survey service?
            </h3>
            <div className="flex gap-3 mb-4">
              {[1, 2, 3, 4, 5].map((rating) => (
                <button
                  key={rating}
                  type="button"
                  onClick={() => handleRatingChange(rating)}
                  className={`w-12 h-12 rounded-full text-white font-semibold transition-all ${
                    feedback.rating === rating
                      ? 'bg-cyan-400 scale-110 shadow-lg'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                >
                  {rating}
                </button>
              ))}
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Any Feedback? (Put anything you wish, e.g. complaints, how can we improve, what made your survey a positive experience etc.)
              </label>
              <textarea
                value={feedback.comments}
                onChange={handleCommentsChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Your feedback..."
              />
            </div>
          </div>

          {/* Surveyor Sign Off Section */}
          <div className="mb-8 border-t pt-6">
            <h3 className="text-lg font-semibold text-blue-700 mb-4">Surveyor Sign Off</h3>

            {/* Surveyor Statement 1 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that I have collected all evidence as per the Retrofit Assessment scheme
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('surveyor1', surveyorStatement1Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  <Edit size={16} />
                  {surveyorSignatures.statement1 ? 'Change Signature' : 'Add Signature'}
                </button>
                {surveyorSignatures.statement1 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={surveyorSignatures.statement1} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {surveyorSignatures.statement1Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Surveyor Statement 2 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that all the data collected by me will follow GDPR rules and regulations as per Eco Approach Ltd's standard
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('surveyor2', surveyorStatement2Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  <Edit size={16} />
                  {surveyorSignatures.statement2 ? 'Change Signature' : 'Add Signature'}
                </button>
                {surveyorSignatures.statement2 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={surveyorSignatures.statement2} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {surveyorSignatures.statement2Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Surveyor Statement 3 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I have informed the customer that measures to be fitted in the property will be processed by the head office and will get back to you as per PAS 2035 Process.
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('surveyor3', surveyorStatement3Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  <Edit size={16} />
                  {surveyorSignatures.statement3 ? 'Change Signature' : 'Add Signature'}
                </button>
                {surveyorSignatures.statement3 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={surveyorSignatures.statement3} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {surveyorSignatures.statement3Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Surveyor Statement 4 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that I have ensured customer property and GDPR details are well taken care while doing my job.
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('surveyor4', surveyorStatement4Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  <Edit size={16} />
                  {surveyorSignatures.statement4 ? 'Change Signature' : 'Add Signature'}
                </button>
                {surveyorSignatures.statement4 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={surveyorSignatures.statement4} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {surveyorSignatures.statement4Date}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Surveyor Statement 5 */}
            <div className="border border-gray-200 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <span className="text-red-500 font-bold">* </span>
                I can confirm that my survey will be subject to compliance check and if they compliance is not approved I will not be getting paid for the job.
              </p>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => openSignatureModal('surveyor5', surveyorStatement5Ref)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  <Edit size={16} />
                  {surveyorSignatures.statement5 ? 'Change Signature' : 'Add Signature'}
                </button>
                {surveyorSignatures.statement5 && (
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <img src={surveyorSignatures.statement5} alt="Signature" className="h-16 border rounded" />
                    <span className="text-sm text-gray-600 whitespace-nowrap">Date: {surveyorSignatures.statement5Date}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-gray-200 flex flex-col sm:flex-row gap-3 sm:justify-between">
          <button
            type="button"
            onClick={onNavigateBack}
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 font-semibold rounded-lg shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
          >
            <ArrowLeft size={18} /> Previous
          </button>
          <button
            type="submit"
            className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 transition-all duration-200 transform hover:scale-[1.02]"
          >
            Continue to Export
          </button>
        </div>
      </form>

      {/* Signature Modal */}
      {currentSignatureRef && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSave={handleSaveSignature}
          onClear={handleClearSignature}
          onDelete={handleDeleteSignature}
          title={currentSignatureType.startsWith('customer') ? 'Customer Signature' : 'Surveyor Signature'}
          signatureRef={currentSignatureRef}
          hasExistingSignature={(() => {
            if (currentSignatureType.startsWith('customer')) {
              const statementNum = currentSignatureType.replace('customer', '');
              return !!customerSignatures[`statement${statementNum}` as keyof CustomerSignature];
            } else if (currentSignatureType.startsWith('surveyor')) {
              const statementNum = currentSignatureType.replace('surveyor', '');
              return !!surveyorSignatures[`statement${statementNum}` as keyof SurveyorSignature];
            }
            return false;
          })()}
        />
      )}

      {/* Hidden signature canvases for refs */}
      <div style={{ display: 'none' }}>
        <SignatureCanvas ref={customerStatement1Ref} />
        <SignatureCanvas ref={customerStatement2Ref} />
        <SignatureCanvas ref={customerStatement3Ref} />
        <SignatureCanvas ref={customerStatement4Ref} />
        <SignatureCanvas ref={surveyorStatement1Ref} />
        <SignatureCanvas ref={surveyorStatement2Ref} />
        <SignatureCanvas ref={surveyorStatement3Ref} />
        <SignatureCanvas ref={surveyorStatement4Ref} />
        <SignatureCanvas ref={surveyorStatement5Ref} />
      </div>
    </div>
  );
};

export default RetrofitSignaturesForm;
